import { useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin, Tooltip } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  ExclamationOutlined,
  StarOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import "../../styles/aiInsights.css";
import AIObservationModal from "../../components/modals/AIObservationModal";
import { useFetchFunction } from "../../utils/hooks/UseFetchUrl";

const SECTION_CONFIG = {
  success: {
    title: "What went well",
    subtitle: "Positive observations identified across your audits.",
    icon: <CheckOutlined />,
    apiKey: "went_well",
  },
  critical: {
    title: "What didn't go well",
    subtitle: "Negative observations / gaps identified across your audits.",
    icon: <ExclamationOutlined />,
    apiKey: "did_not_go_well",
  },
  warning: {
    title: "What can be improved",
    subtitle:
      "Areas where the audit findings indicate an opportunity for improvement.",
    icon: <StarOutlined />,
    apiKey: "can_be_improved",
  },
};

const getUpdatedTime = (date) => {
  if (!date) return "Not updated yet";

  const updatedDate = new Date(date);
  const now = new Date();

  const diffInSeconds = Math.floor((now - updatedDate) / 1000);

  if (diffInSeconds < 60) return "Just now";

  const diffInMinutes = Math.floor(diffInSeconds / 60);

  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);

  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);

  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);

  return `${diffInYears} year${diffInYears !== 1 ? "s" : ""} ago`;
};

const UPDATE_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

const normalizeAiInsightDate = (date) => {
  if (date === null || date === undefined) return null;

  if (typeof date === "string") {
    const value = date.trim();

    if (
      value === "" ||
      value.toLowerCase() === "n/a" ||
      value.toLowerCase() === "na" ||
      value.toLowerCase() === "null" ||
      value.toLowerCase() === "undefined"
    ) {
      return null;
    }

    return value;
  }

  return date;
};

const parseModifiedAnalysisDate = (date) => {
  const normalizedDate = normalizeAiInsightDate(date);

  if (normalizedDate === null) {
    return null;
  }

  const updatedDate = new Date(normalizedDate);

  if (Number.isNaN(updatedDate.getTime())) {
    return null;
  }

  return updatedDate;
};

const getUpdateStatus = (date) => {
  const updatedDate = parseModifiedAnalysisDate(date);

  if (!updatedDate) {
    return {
      canUpdate: true,
      nextUpdateDate: null,
    };
  }

  const nextUpdateDate = new Date(
    updatedDate.getTime() + UPDATE_INTERVAL_MS,
  );

  const canUpdate = Date.now() >= nextUpdateDate.getTime();

  return {
    canUpdate,
    nextUpdateDate,
  };
};

const formatUpdateDate = (date) => {
  if (!date) return "";

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const AIInsights = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const updateInProgressRef = useRef(false);
  const [auditType, setAuditType] = useState(null);
  const [modalInfo, setModalInfo] = useState({
    showModal: false,
    modalTitle: "",
    selectedAuditStores: [],
  });

  const {
    loading: auditCycleLoading,
    data: auditCycleData,
    getData: getAuditCyclesData,
  } = useFetchFunction();

  const {
    loading: insightsLoading,
    data: insightsData,
    getData: getInsightsData,
  } = useFetchFunction();

  useEffect(() => {
    getAuditCyclesData("audit_cycle_for_dashboard");
  }, []);

  useEffect(() => {
    if (!auditCycleData?.length) return;

    const latestCycle = auditCycleData.reduce((latest, cycle) => {
      return Number(cycle.id) > Number(latest.id) ? cycle : latest;
    });

    setAuditType(latestCycle.id);
  }, [auditCycleData]);

  useEffect(() => {
    if (!auditType) return;
    getInsightsData(`audit_cycle/${auditType}/cycle_ai_insights`);
  }, [auditType]);

  useEffect(() => {
    if (!isUpdating) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isUpdating]);

  // Group API records by point_key.
  const groupedInsights = useMemo(() => {
    if (!insightsData || typeof insightsData !== "object") {
      return {
        success: [],
        critical: [],
        warning: [],
      };
    }

    return {
      success: Array.isArray(insightsData.went_well)
        ? insightsData.went_well
        : [],

      critical: Array.isArray(insightsData.did_not_go_well)
        ? insightsData.did_not_go_well
        : [],

      warning: Array.isArray(insightsData.can_be_improved)
        ? insightsData.can_be_improved
        : [],
    };
  }, [insightsData]);

  const handleCloseModal = () => {
    setModalInfo({
      showModal: false,
      modalTitle: "",
      selectedAuditStores: [],
    });
  };

  const handleFilterChange = (value) => {
    setAuditType(value);
  };

  const handleViewAudits = (sectionKey, point) => {
    const auditStoreIds =
      point?.audit_stores?.map((auditStore) =>
        Number(auditStore.audit_store_id),
      ) || [];

    setModalInfo({
      showModal: true,
      modalTitle: `${SECTION_CONFIG[sectionKey]?.title || "AI Observations"} in ${
        selectedCycle?.name || "Selected Cycle"
      }`,
      selectedAuditStores: auditStoreIds,
    });
  };

  // const handleHeaderCountClick = (sectionKey) => {
  //   setSelectedSection(sectionKey);
  //   setSelectedPointKey(null);
  //   setShowModal(true);
  // };

  const selectedCycle = auditCycleData?.find(
    (cycle) => Number(cycle.id) === Number(auditType),
  );

  const updateStatus = useMemo(
    () => getUpdateStatus(insightsData?.modified_ai_analysis_date),
    [insightsData?.modified_ai_analysis_date],
  );

  const updateDisabled = isUpdating || !updateStatus.canUpdate;
  const isLoading = auditCycleLoading || insightsLoading;

  const handleGetUpdate = async () => {
    if (!auditType || updateDisabled || updateInProgressRef.current) return;

    updateInProgressRef.current = true;
    setIsUpdating(true);

    try {
      const bulkResult = await getInsightsData(
        `audit_cycle/${auditType}/bulk_ai_insights_with_key_points`,
      );

      if (!bulkResult?.success) {
        return;
      }

      await getInsightsData(`audit_cycle/${auditType}/cycle_ai_insights`);
    } finally {
      updateInProgressRef.current = false;
      setIsUpdating(false);
    }
  };

  return (
    <div className="aii-page">
      <header className="aii-header">
        <div className="aii-title-wrap">
          <div className="aii-spark-lg">
            <span className="geminiSparkle">✦</span>
          </div>

          <div>
            <h2 className="aii-title">AI Insights</h2>

            <p className="aii-subtitle">
              Understand the overall customer experience across your audits.
            </p>
          </div>
        </div>

        <div className="aii-filter">
          <AppstoreOutlined className="aii-filter-icon" />

          <span className="aii-filter-label">Type of Audit</span>

          <Select
            className="aii-filter-select"
            value={auditType}
            options={
              auditCycleData?.map((cycle) => ({
                value: cycle.id,
                label: cycle.name,
              })) || []
            }
            onChange={handleFilterChange}
            loading={auditCycleLoading}
            disabled={auditCycleLoading || !auditCycleData?.length}
            popupMatchSelectWidth={false}
          />

          <span className="aii-filter-updated">
            Last updated &nbsp;: &nbsp;
            {getUpdatedTime(insightsData?.modified_ai_analysis_date)}
          </span>
          {/* <button className="aii-ask-btn" onClick={handleGetUpdate}>
            <span className="geminiSparkle">✦</span>
            Update
          </button> */}
          <Tooltip
            title={
              updateDisabled && !isUpdating
                ? `Update available on ${formatUpdateDate(
                    updateStatus.nextUpdateDate,
                  )}`
                : isUpdating
                  ? "AI insights are being updated. Please wait."
                  : ""
            }
          >
            <button
              className="aii-ask-btn"
              onClick={handleGetUpdate}
              disabled={updateDisabled}
            >
              <span className="geminiSparkle">✦</span>

              {isUpdating ? "Updating..." : "Update"}
            </button>
          </Tooltip>
        </div>
      </header>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>
          <section className="aii-overview">
            <div className="aii-overview-text">
              <div className="aii-spark-sm">
                <span className="geminiSparkle">✦</span>
              </div>

              <div>
                <h3 className="aii-overview-title">Overall Observation</h3>

                <p className="aii-overview-desc">
                  AI observations generated from the selected audit cycle.
                </p>
              </div>
            </div>

            <div className="aii-stats">
              {Object.entries(SECTION_CONFIG).map(([sectionKey, config]) => {
                const total = groupedInsights[sectionKey]?.length || 0;

                return (
                  <div
                    className="aii-stat"
                    key={sectionKey}
                    // onClick={() => handleHeaderCountClick(sectionKey)}
                  >
                    <div className={`aii-stat-icon ${sectionKey}`}>
                      {config.icon}
                    </div>

                    <div>
                      <div className={`aii-stat-count ${sectionKey}`}>
                        {total}
                      </div>

                      <div className="aii-stat-label">
                        {sectionKey === "success"
                          ? "Positive Observations"
                          : sectionKey === "critical"
                            ? "Negative Observations"
                            : "Improvement Areas"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="aii-grid">
            {Object.entries(SECTION_CONFIG).map(([sectionKey, config]) => {
              const points = groupedInsights[sectionKey];

              return (
                <section className={`aii-card ${sectionKey}`} key={sectionKey}>
                  <div className="aii-card-head">
                    <div className={`aii-card-icon ${sectionKey}`}>
                      {config.icon}
                    </div>

                    <div className="aii-card-head-text">
                      <h3 className="aii-card-title">{config.title}</h3>

                      <p className="aii-card-sub">{config.subtitle}</p>
                    </div>

                    <span className={`aii-pill ${sectionKey}`}>
                      {points.reduce(
                        (sum, item) => sum + (item.audit_stores?.length || 0),
                        0,
                      )}
                      &nbsp; Observations
                    </span>
                  </div>

                  <ul className="aii-list">
                    {points.map((item) => (
                      <li
                        className="aii-item"
                        key={`${sectionKey}-${item.point}`}
                      >
                        <p className="aii-item-text">{item.point}</p>

                        <div className="aii-item-meta">
                          <span className={`aii-pill ${sectionKey}`}>
                            {item.audit_stores?.length || 0} audits
                          </span>

                          <button
                            type="button"
                            className="aii-link"
                            onClick={() => handleViewAudits(sectionKey, item)}
                          >
                            View audits <ArrowRightOutlined />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </>
      )}

      <AIObservationModal
        open={modalInfo.showModal}
        selectedCycleId={auditType}
        title={modalInfo.modalTitle}
        auditStoreIds={modalInfo.selectedAuditStores}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default AIInsights;
